<template>
  <div class="sttdfiltrs">
    <slot></slot>

    <div
      v-if="!isPreflop"
      class="sttdfiltrs_top"
    >
      <StudyFilterSuits
        :namespace="namespace"
        :applyFilterActionName="applyFilterActionName"
        class="sttdfiltrs_suits"
        style="--mobile-display: grid; margin-bottom: var(--ff-gap)"
      />
      <slot name="filter_pills"></slot>
    </div>

    <div class="position-relative flex-1">
      <MessageOverlaySkeleton
        v-if="!showFlopContent"
        :isBlurred="true"
        :isTopDown="true"
        data-tst="study_filters_message"
        class="gw_mesblock_container"
        style="
          --content-margin: auto auto auto auto;
          --radius: 6px;
          --back-from: var(--clr-black-0);
          --back-to: var(--clr-black-0);
        "
      >
        <MessagesGoPostflop
          :namespace="gamestateNamespace"
          :canAISolve="canAISolve"
          class="ma-auto gw_mesblock_container"
          :title="$t(`messages.select_postflop_spot_categories`)"
        />
      </MessageOverlaySkeleton>
      <div
        v-else
        class="sttdfiltrs_content"
      >
        <div class="sttdfiltrs_content_column">
          <TrainerRangeCategories
            class="sttdfiltrs_item"
            style="--gap: var(--ff-gap)"
            :hasHands="true"
            :hasEqBuckets="true"
            :hasDraws="false"
            :hasEqBucketsAdvanced="false"
            :catHands="catHands"
            :catEqBuckets="catEqBuckets"
            :hasArrowBtn="hasArrowBtn"
            :hasFilterBtn="hasFilterBtn"
            :useHex="useHex"
            :filterCategories="filterCategories"
            @cat:mouseenter="category => handleCatMouseEnter(category)"
            @cat:mouseleave="() => handleCatMouseEnter(null)"
            @cat:click="handleCatClick"
            @cat:filterclick="handleCatFilterClick"
            @cat:arrowclick="handleCatArrowClick"
          >
            <template #title_hands>
              <div class="sttdfiltrs_item_title text_tab_small">
                {{ $t(`common.hands`) }}
                <StudyRangeLengthSwitcher class="gw_hvr_top_content ml-1" />
              </div>
            </template>
            <template #title_eq_buckets>
              <div class="sttdfiltrs_item_title text_tab_small gw_hvr">
                {{ $t(`switchers.swv_eq_buckets_simple`) }}
                <div
                  class="mdi mdi-help-circle-outline csst mr-auto gw_hvr_content ml-1"
                  @mouseenter="e => fl_positionTooltip(e, { placement: 'top' })"
                >
                  <div
                    class="csst_content clr-white"
                    style="position: fixed"
                    v-html="$t(`browse.eq_buckets.eq_buckets_simple_tooltip`)"
                  ></div>
                </div>
                <StudyRangeLengthSwitcher class="gw_hvr_top_content ml-1" />
              </div>
            </template>
          </TrainerRangeCategories>
        </div>
        <div class="sttdfiltrs_content_column">
          <TrainerRangeCategories
            class="sttdfiltrs_item"
            style="--gap: var(--ff-gap)"
            :hasHands="false"
            :hasEqBuckets="false"
            :hasDraws="true"
            :hasEqBucketsAdvanced="true"
            :catDraws="catDraws"
            :catEqBucketsAdvanced="catEqBucketsAdvanced"
            :hasArrowBtn="hasArrowBtn"
            :hasFilterBtn="hasFilterBtn"
            :useHex="useHex"
            :filterCategories="filterCategories"
            @cat:mouseenter="category => handleCatMouseEnter(category)"
            @cat:mouseleave="() => handleCatMouseEnter(null)"
            @cat:click="handleCatClick"
            @cat:filterclick="handleCatFilterClick"
            @cat:arrowclick="handleCatArrowClick"
          >
            <template #title_draws>
              <div class="sttdfiltrs_item_title text_tab_small">
                {{ $t(`common.draws`) }}
                <StudyRangeLengthSwitcher class="gw_hvr_top_content ml-1" />
              </div>
            </template>
            <template #title_eq_buckets_advanced>
              <div class="sttdfiltrs_item_title text_tab_small">
                {{ $t(`switchers.swv_eq_buckets_advanced`) }}
                <StudyRangeLengthSwitcher class="gw_hvr_top_content ml-1" />
              </div>
            </template>
          </TrainerRangeCategories>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import MessagesGoPostflop from '@/components/Poker/Overlays/Messages/MessagesGoPostflop'
import MessageOverlaySkeleton from '@/components/Poker/Overlays/MessageOverlaySkeleton'
import { SNK } from '@/helpers/helpers'
import TrainerRangeCategories from '@/components/Trainer/TrainerRangeCategories'
import float_mix from '@/mixins/float_mix'
import StudyFilterSuits from '@/components/Study/StudyFilterSuits'
import StudyRangeLengthSwitcher from '@/components/Switchers/StudyRangeLengthSwitcher'

export default {
  name: 'StudyFilters',
  components: {
    TrainerRangeCategories,
    MessagesGoPostflop,
    MessageOverlaySkeleton,
    StudyFilterSuits,
    StudyRangeLengthSwitcher,
  },
  mixins: [float_mix],
  props: {
    namespace: String,
    gamestateNamespace: String,
    canAISolve: { required: true, type: Boolean },
    showFlopContent: Boolean,
    isPreflop: Boolean,
    catHands: { default: () => [] },
    catDraws: { default: () => [] },
    catEqBuckets: { default: () => [] },
    catEqBucketsAdvanced: { default: () => [] },
    applyFilterActionName: { default: () => 'sol_data_applyFilter' },
    // catEqBuckets: { default: () => [] },
    filterCategories: { default: () => [] },
    // solData: Object,
    useHex: Boolean,
    mobile: Boolean,
    mode: { default: 'study' },
    hasArrowBtn: { type: Boolean, default: true },
    hasFilterBtn: Boolean,
  },
  emits: ['click:category'],
  methods: {
    handleCatMouseEnter(category) {
      this.$store.dispatch(SNK('sol_detail_highlight', this.namespace), category)
    },
    handleCatClick(category) {
      if (
        this.mode === 'builder' &&
        this.filterCategories.length &&
        this.filterCategories.some(c => c.name !== category.name)
      ) {
        // in builder mode we can filter and paint together. If filter is active and we click on category that is not filtered
        // we need to add it to filter and then paint category via click:category
        this.handleCatFilterClick(category)
        nextTick(() => {
          this.$emit('click:category', category)
        })
        return
      }

      this.$emit('click:category', category)

      if (this.mode === 'study') {
        this.handleCatFilterClick(category)
      }
    },
    handleCatFilterClick(category) {
      this.$store.dispatch(SNK('sol_filters_toggleCategory', this.namespace), {
        category,
        applyFilterActionName: this.applyFilterActionName,
      })
    },
    handleCatArrowClick(categories) {
      this.$store.dispatch('sol_filters_toggleCategories', {
        categories,
        applyFilterActionName: this.applyFilterActionName,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.sttdfiltrs {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  --ff-gap: 1rem;
  flex: 1;
  // gap: var(--ff-gap);
  &_content {
    min-height: 0;
    min-width: 0;
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    gap: var(--ff-gap);
    &_column {
      flex: 1;
      min-width: 16rem;
      display: flex;
      gap: var(--ff-gap);
      flex-direction: column;
    }
  }
  &_top {
    display: flex;
    // align-items: center;
    // justify-content: space-between;
    // min-width: 0;
    // min-height: 0;
  }
  &_item {
    // min-width: calc(50% - 0.5rem);
    &_title {
      display: flex;
      justify-content: space-between;
      padding: 0 0 0.25rem 0;
    }
  }
}
</style>
